/**
 * useModuleConfig - Hook para gestión de configuración de módulo
 *
 * @remarks
 * Proporciona estado y acciones para cargar, modificar y guardar
 * la configuración de entidades de un módulo específico.
 *
 * @example
 * ```tsx
 * const { config, isLoading, error, updateEntity, resetEntity } = useModuleConfig(
 *   projectId,
 *   'environmental'
 * );
 *
 * // Actualizar configuración de árboles
 * await updateEntity('tree', { edgeThresholdMeters: 25 });
 * ```
 */

import { useState, useEffect, useCallback } from 'react';
import {
  moduleConfigApi,
  type ModuleConfigResponse,
  type ModuleEntityConfig,
} from '../../spatial/services/module-config-api.service';

/**
 * Estado del hook de configuración de módulo
 */
export interface UseModuleConfigState {
  /** Configuración completa del módulo */
  config: ModuleConfigResponse | null;
  /** Indica si está cargando */
  isLoading: boolean;
  /** Indica si está guardando */
  isSaving: boolean;
  /** Error si ocurrió alguno */
  error: string | null;
  /** Recargar configuración */
  reload: () => Promise<void>;
  /** Actualizar configuración de una entidad */
  updateEntity: (entityType: string, entityConfig: ModuleEntityConfig) => Promise<void>;
  /** Resetear configuración de una entidad a valores por defecto */
  resetEntity: (entityType: string) => Promise<void>;
}

/**
 * Hook para gestión de configuración de módulo
 *
 * @param projectId - ID del proyecto
 * @param moduleId - ID del módulo (environmental, waste, mobility, etc.)
 * @returns Estado y acciones para la configuración
 */
export function useModuleConfig(
  projectId: string | null,
  moduleId: string | null,
): UseModuleConfigState {
  const [config, setConfig] = useState<ModuleConfigResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Cargar configuración del módulo
   */
  const loadConfig = useCallback(async () => {
    if (!projectId || !moduleId) {
      setConfig(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log('[useModuleConfig] Loading config for:', moduleId);
      const response = await moduleConfigApi.getConfig(projectId, moduleId);
      setConfig(response);
      console.log('[useModuleConfig] Config loaded:', response);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error desconocido';
      console.error('[useModuleConfig] Error loading config:', message);
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [projectId, moduleId]);

  /**
   * Actualizar configuración de una entidad
   */
  const updateEntity = useCallback(
    async (entityType: string, entityConfig: ModuleEntityConfig) => {
      if (!projectId || !moduleId) {
        throw new Error('projectId y moduleId son requeridos');
      }

      setIsSaving(true);
      setError(null);

      try {
        console.log('[useModuleConfig] Updating entity:', entityType, entityConfig);
        const response = await moduleConfigApi.updateEntityConfig(
          projectId,
          moduleId,
          entityType,
          entityConfig,
        );
        setConfig(response);
        console.log('[useModuleConfig] Entity updated:', response);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Error desconocido';
        console.error('[useModuleConfig] Error updating entity:', message);
        setError(message);
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [projectId, moduleId],
  );

  /**
   * Resetear configuración de una entidad a valores por defecto
   */
  const resetEntity = useCallback(
    async (entityType: string) => {
      if (!projectId || !moduleId) {
        throw new Error('projectId y moduleId son requeridos');
      }

      setIsSaving(true);
      setError(null);

      try {
        console.log('[useModuleConfig] Resetting entity:', entityType);
        const response = await moduleConfigApi.resetEntityConfig(
          projectId,
          moduleId,
          entityType,
        );
        setConfig(response);
        console.log('[useModuleConfig] Entity reset:', response);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Error desconocido';
        console.error('[useModuleConfig] Error resetting entity:', message);
        setError(message);
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [projectId, moduleId],
  );

  // Cargar configuración cuando cambian los parámetros
  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  return {
    config,
    isLoading,
    isSaving,
    error,
    reload: loadConfig,
    updateEntity,
    resetEntity,
  };
}
